import type { CompositionEditorRow } from './compositionEditorAdapter';
import type { CompositionEditGateResult } from './compositionEditGate';

export type CompositionRowActionKind =
  | 'EXCLUDE'
  | 'RESTORE'
  | 'REPLACE'
  | 'ADD_DOCUMENT'
  | 'PREVIEW'
  | 'DOWNLOAD';

export type CompositionRowAction = {
  kind: CompositionRowActionKind;
  label: string;
};

const ACTION_LABELS: Record<CompositionRowActionKind, string> = {
  EXCLUDE: 'Исключить',
  RESTORE: 'Восстановить',
  REPLACE: 'Заменить',
  ADD_DOCUMENT: 'Привязать документ',
  PREVIEW: 'Просмотреть документ',
  DOWNLOAD: 'Скачать документ',
};

/** Stable menu order for row ⋮ dropdown (FE-005A-4+). */
const ACTION_ORDER: CompositionRowActionKind[] = [
  'ADD_DOCUMENT',
  'REPLACE',
  'EXCLUDE',
  'RESTORE',
  'PREVIEW',
  'DOWNLOAD',
];

export type CompositionRowActionContext = {
  row: CompositionEditorRow;
  editGate: CompositionEditGateResult;
};

function hasLinkedDocument(row: CompositionEditorRow): boolean {
  return row.linkedDocumentId != null || row.item.documents[0]?.id != null;
}

function createAction(kind: CompositionRowActionKind): CompositionRowAction {
  return { kind, label: ACTION_LABELS[kind] };
}

/** Action factory — builds ordered action descriptors from visibility rules. */
export function buildCompositionRowActions(kinds: CompositionRowActionKind[]): CompositionRowAction[] {
  const unique = new Set(kinds);
  return ACTION_ORDER.filter((kind) => unique.has(kind)).map(createAction);
}

function resolveVisibleActionKinds(context: CompositionRowActionContext): CompositionRowActionKind[] {
  const { row, editGate } = context;
  const documentActions: CompositionRowActionKind[] = hasLinkedDocument(row)
    ? ['PREVIEW', 'DOWNLOAD']
    : [];

  if (!editGate.canEdit) {
    return documentActions;
  }

  if (row.compositionStatus === 'EXCLUDED') {
    return ['RESTORE', ...documentActions];
  }

  if (row.compositionStatus !== 'INCLUDED') {
    return [];
  }

  if (row.state === 'MISSING' || !hasLinkedDocument(row)) {
    const actions: CompositionRowActionKind[] = ['ADD_DOCUMENT'];
    if (row.required) {
      actions.push('EXCLUDE');
    }
    return actions;
  }

  // INCLUDED with linked document (catalog slot or manual).
  return ['REPLACE', 'EXCLUDE', ...documentActions];
}

/**
 * Pure action engine: CompositionEditorRow + EditGate → available row actions.
 * Does not execute mutations or open UI.
 */
export function resolveCompositionRowActions(
  row: CompositionEditorRow,
  editGate: CompositionEditGateResult
): CompositionRowAction[] {
  return buildCompositionRowActions(
    resolveVisibleActionKinds({
      row,
      editGate,
    })
  );
}

export type CompositionEditorDisplayRow = CompositionEditorRow & {
  availableActions: CompositionRowAction[];
};

export function attachCompositionRowActions(
  rows: CompositionEditorRow[],
  editGate: CompositionEditGateResult
): CompositionEditorDisplayRow[] {
  return rows.map((row) => ({
    ...row,
    availableActions: resolveCompositionRowActions(row, editGate),
  }));
}

export type PartitionedRowActions = {
  /** Standalone upload / bind control (ADD_DOCUMENT). */
  primary: CompositionRowAction | null;
  /** Eye button action (PREVIEW). */
  preview: CompositionRowAction | null;
  /** Download button action (DOWNLOAD). */
  download: CompositionRowAction | null;
  /** ⋮ dropdown items — all remaining actions in Action Engine order. */
  menu: CompositionRowAction[];
};

/** Splits Action Engine output for RowActions presentation — no kind checks in UI. */
export function partitionRowActionsForPresentation(
  actions: CompositionRowAction[]
): PartitionedRowActions {
  const preview = actions.find((action) => action.kind === 'PREVIEW') ?? null;
  const download = actions.find((action) => action.kind === 'DOWNLOAD') ?? null;
  const primary = actions.find((action) => action.kind === 'ADD_DOCUMENT') ?? null;
  const menu = actions.filter(
    (action) =>
      action.kind !== 'PREVIEW' &&
      action.kind !== 'DOWNLOAD' &&
      action.kind !== 'ADD_DOCUMENT'
  );

  return { primary, preview, download, menu };
}
